import { httpClient } from "infra/axios/axios-api";
import { Document } from "domain/entities/document";
import { DocumentType } from "domain/entities/document-type";
import { Parameter } from "domain/entities/parameter";
import { EMAIL } from "domain/enum/email";
import { DocumentTypeInputFilter } from "domain/models/document-type-input-filter";
import { translateText } from "infra/translation/translate";
import DropdownComponent from "presentation/components/Shared/DropdownComponent";
import InputDateComponent from "presentation/components/Shared/InputDateComponent";
import Spinner from "presentation/components/Shared/Spinner";
import { useCreateCPSDocumentMutation } from "presentation/hooks/use-query-document";
import { useDocumentTypeByFilter } from "presentation/hooks/use-query-document-type";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { FileUpload } from "primereact/fileupload";
import { Toast } from "primereact/toast";
import { RefObject, useEffect, useState } from "react";

interface ParamProps {
  isDisable: boolean;
  onClose?: () => void;
  toastRef?: RefObject<Toast | null>;
}

interface DocumentTypeOption {
  id: string;
  value: string;
  type: string
}

interface InvoiceParams {
  date: Date;
  documentType: DocumentTypeOption
}


const InvoiceForm: React.FC<ParamProps> = (params: ParamProps) => {

  const { email: userEmployeeEmail, userEmployee: userEmployee, userPermissions } = useAuthStore();

  const {
      mutate: createCPSDocumentMutation,
      isError: isCreateCPSDocError,
      isSuccess: isCreateCPSDocSuccess,
      isPending: isCreateCPSDocumentPending
    } = useCreateCPSDocumentMutation();

  const { data: documentTypes } =
    useDocumentTypeByFilter({ limit: 100, justCPStypes: true } as DocumentTypeInputFilter);

  const [contaboResponse, setContaboResponse] = useState<any>();
  const [isContaboPending, setIsContaboPending] = useState(false);
  const [documentTypeOptions, setDocumentTypeOptions] = useState<Parameter[]>([]);
  const [doc, setDoc] = useState<{ file: File, name: string; url: string } | null>(null);
  const [invoiceParameters, setInvoiceParameters] = useState<InvoiceParams>();
  const [showDoc, setShowDoc] = useState(false);


  useEffect(() => {
    if (documentTypes?.data) {
      setDocumentTypeOptions(
        documentTypes.data.map((item: DocumentType) => ({
          id: item.id ?? "",
          value: item.name,
          type: "documentType"
        }))
      );
    }
  }, [documentTypes]);

  useEffect(() => {
    if (contaboResponse && invoiceParameters?.date && invoiceParameters.documentType) {

      const month = new Date(invoiceParameters.date).getMonth() + 1;
      const year = new Date(invoiceParameters.date).getFullYear();

      const filePath = contaboResponse.data.filePath;

      const cpsDocumentToCreate: Document = {
        id: undefined,
        fullPath: filePath,
        documentType: { id: invoiceParameters.documentType.id },
        employee: { id: userEmployee?.id ?? "" },
        month: month,
        year: year,
        lastModifiedBy: userEmployeeEmail ?? EMAIL,
      };

      setIsContaboPending(false);

      createCPSDocumentMutation(cpsDocumentToCreate);
    }
  }, [contaboResponse]);

  useEffect(() => {
    if (isCreateCPSDocError) {
      params.toastRef?.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Falha ao guardar documentos",
        life: 1500,
      });
    }
  }, [isCreateCPSDocError]);

  useEffect(() => {
    if (isCreateCPSDocSuccess) {
      params.toastRef?.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Documento guardado com sucesso",
        life: 1500,
      });

      params.onClose?.(); // fecha modal logo
    }
  }, [isCreateCPSDocSuccess]);


  const handleSave = async () => {
    
    if (doc && invoiceParameters?.date && invoiceParameters.documentType) {
      
      const month = new Date(invoiceParameters.date).getMonth() + 1;
      const year = new Date(invoiceParameters.date).getFullYear(); 

      const formData = new FormData();

      const extension = doc.name.split(".").pop();
      const safeDocName = `${month}_${year}_${invoiceParameters.documentType.id}_${Date.now()}`

      const fileNameWithExtension = `${safeDocName}.${extension}`;
      formData.append("file", doc.file, fileNameWithExtension);

      try {
        setIsContaboPending(true);

        const response = await httpClient.post(
          `/assets/invoices/${userEmployee?.id}`,
          formData
        );

        setContaboResponse(response);

      } catch (error) {
        console.error("Erro no upload:", error);
      }
    }
  };

  const handleUpload = async (event: any) => {
    const file = event.files[0];
    if (!file) return;

    setDoc({
      file,
      name: file.name,
      url: URL.createObjectURL(file),
    });
  };

  const removeDocument = () => {
    setDoc(null);
  };

  const openDocument = () => {
    if (doc?.url) setShowDoc(true); 
  };

  if (isCreateCPSDocumentPending || isContaboPending) {
    return (
      <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <Spinner />
      </div>
    );
  }

  return (
    <div>

      <div className="default-grid header-spacing">

        <div className="default-4-cols">
          <DropdownComponent
            options={documentTypeOptions}
            isValueId={true}
            label="Tipo de Documento"
            fieldName="documentType"
            currentObject={invoiceParameters}
            getValue={invoiceParameters?.documentType?.id}
            setValue={setInvoiceParameters}
          />
        </div>
        
        <div className="default-4-cols">
          <InputDateComponent
            label="Mês/Ano"
            fieldName="date"
            view="month"
            dateFormat="mm/yy"
            currentObject={invoiceParameters}
            getValue={invoiceParameters?.date}
            setValue={setInvoiceParameters}
          />
        </div>

        {/* Caixa do documento */}
        <div className="default-4-cols">
          <div className="flex flex-column align-items-center border-1 document-border-color p-3">
            {doc ? (
              <>
                <p className="font-bold">{translateText(doc.name, "pt")}</p>
                <div className="spacing-between-buttons">
                  <Button
                    label={translateText("Ver", "pt")}
                    icon="pi pi-eye"
                    className="p-button-sm p-button-outlined"
                    onClick={openDocument}
                  />
                  <Button
                    label={translateText("Remover", "pt")}
                    icon="pi pi-trash"
                    className="p-button-sm p-button-outlined"
                    severity="danger"
                    onClick={removeDocument}
                  />
                </div>
              </>
            ) : (
              <FileUpload
                mode="basic"
                accept=".pdf,.jpg,.png"
                maxFileSize={10000000}
                customUpload
                auto
                uploadHandler={handleUpload}
                chooseLabel={translateText("Upload", "pt")}
                className="p-mt-2"
              />
            )}
          </div>
        </div>
        <div>{ }</div>
        <div>{ }</div>
        <div>{ }</div>
        <div className="default-4-cols">
          <div style={{ display: "flex", justifyContent: "center" }}>
              <Button 
                label="Adicionar" 
                onClick={handleSave} 
                disabled={!invoiceParameters?.date || !invoiceParameters?.documentType || !doc}
              />
          </div>
        </div>

      </div>

      <Dialog
        header="Visualização do Documento"
        visible={showDoc}
        style={{ width: "80vw", height: "90vh" }}
        onHide={() => {
          setShowDoc(false);
        }}
      >
        {doc && (doc.name.includes('.pdf') ? (
          <iframe
            src={`${doc.url}#navpanes=0`}
            width="100%"
            height="700px"
            title="Invoice"
            style={{ border: "none" }}
          ></iframe>) :
          (
            <img src={doc.url}
              width="100%"
              height="auto"
              title="Invoice"
              style={{ border: "none" }}>
            </img>
          )
        )}
      </Dialog>

    </div>
  );
};
export default InvoiceForm;
